import { useEffect, useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type { AppointmentDto, AppointmentDriverDto } from '@/api/types';
import {
    ApiCallError,
    adminListCarrierDrivers,
    addAppointmentDriver,
    removeAppointmentDriver,
    CarrierDriverAdminRow,
    AddDriverBody,
} from '@/api/client';

/**
 * AssignDriverDialog — manage the list of drivers on an appointment.
 * Scheduler-side twin of the carrier portal's own driver-assignment action;
 * calls the scheduler endpoints (appointmentStatus.ts):
 *   POST   /api/appointments/{id}/drivers                     (add)
 *   DELETE /api/appointments/{id}/drivers/{appointmentDriverId} (remove)
 * both locked against DEPARTED/CANCELLED/NOSHOW appointments server-side.
 *
 * An appointment can have several drivers (multi-truck pickup, or an LTL
 * appointment booked with a longer window — see migration 134). Each add or
 * remove is its own immediate action (not a single batched "Save"), matching
 * how the Settings > Drivers roster tab already works — the list updates right
 * away.
 *
 * Carrier is shown as fixed, read-only context — which carrier owns this
 * appointment is already decided by the time drivers are being assigned, and
 * the roster picker is scoped to that carrier server-side (the scheduler
 * endpoint resolves the appointment's own CarrierCode, not anything supplied
 * by this dialog).
 */

interface AssignDriverDialogProps {
    open: boolean;
    onClose: () => void;
    appt: AppointmentDto;
    getToken: () => Promise<string>;
    /** Called after any add/remove so the parent (Calendar) can refresh its
     *  own data. No payload — the dialog manages its own local list. */
    onAssigned?: () => void;
}

type Mode = 'pick' | 'new';

export function AssignDriverDialog({ open, onClose, appt, getToken, onAssigned }: AssignDriverDialogProps) {
    // Local working copy of the driver list, seeded from the appointment and
    // updated in place as drivers are added/removed (so the UI reflects each
    // action immediately without waiting on a parent refresh).
    const [drivers, setDrivers] = useState<AppointmentDriverDto[]>(appt.drivers);
    const [mode, setMode] = useState<Mode>('pick');
    const [roster, setRoster] = useState<CarrierDriverAdminRow[] | 'loading' | { error: string }>('loading');
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [adding, setAdding] = useState(false);
    const [removingId, setRemovingId] = useState<number | null>(null);
    const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

    useEffect(() => {
        if (!open) return;
        setDrivers(appt.drivers);
        let cancelled = false;
        setRoster('loading');
        (async () => {
            try {
                const token = await getToken();
                const rows = await adminListCarrierDrivers(token, appt.carrierCode);
                if (!cancelled) setRoster(rows);
            } catch (e) {
                if (!cancelled) setRoster({ error: e instanceof ApiCallError ? e.message : (e instanceof Error ? e.message : 'Failed to load roster.') });
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, appt.carrierCode, getToken]);

    const resetAddForm = () => {
        setMode('pick'); setSelectedId(null);
        setName(''); setPhone(''); setEmail('');
    };
    const close = () => { resetAddForm(); setMsg(null); onClose(); };

    const handleAdd = async () => {
        let body: AddDriverBody;
        if (mode === 'pick') {
            if (selectedId == null) { setMsg({ kind: 'err', text: 'Select a driver.' }); return; }
            body = { driverId: selectedId };
        } else {
            if (!name.trim() || !phone.trim()) { setMsg({ kind: 'err', text: 'Name and phone are required.' }); return; }
            body = { name: name.trim(), phone: phone.trim(), email: email.trim() || null };
        }
        setMsg(null);
        setAdding(true);
        try {
            const token = await getToken();
            const result = await addAppointmentDriver(token, appt.appointmentId, body);
            setDrivers(prev => [...prev, {
                appointmentDriverId: result.appointmentDriverId,
                driverId: result.driverId,
                name: result.name,
                phone: result.phone,
            }]);
            resetAddForm();
            setMsg({ kind: 'ok', text: `Added ${result.name}.` });
            onAssigned?.();
        } catch (e) {
            setMsg({ kind: 'err', text: e instanceof ApiCallError ? e.message : (e instanceof Error ? e.message : 'Failed to add driver.') });
        } finally {
            setAdding(false);
        }
    };

    const handleRemove = async (appointmentDriverId: number) => {
        setMsg(null);
        setRemovingId(appointmentDriverId);
        const removed = drivers.find(d => d.appointmentDriverId === appointmentDriverId);
        try {
            const token = await getToken();
            await removeAppointmentDriver(token, appt.appointmentId, appointmentDriverId);
            setDrivers(prev => prev.filter(d => d.appointmentDriverId !== appointmentDriverId));
            setMsg({ kind: 'ok', text: `Removed ${removed?.name ?? 'driver'}.` });
            onAssigned?.();
        } catch (e) {
            setMsg({ kind: 'err', text: e instanceof ApiCallError ? e.message : (e instanceof Error ? e.message : 'Failed to remove driver.') });
        } finally {
            setRemovingId(null);
        }
    };

    const carrierLabel = appt.carrierName || appt.carrierCode;
    const activeRoster = Array.isArray(roster) ? roster.filter(d => d.isActive) : [];

    return (
        <Dialog open={open} onOpenChange={(o) => !o && close()}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Drivers</DialogTitle>
                    <DialogDescription>
                        Appointment {appt.appointmentNumber} — <span className="font-medium text-foreground">{carrierLabel}</span>
                        <span className="block text-xs mt-0.5">Carrier is fixed for this appointment and isn&apos;t editable here.</span>
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                    {/* Add a driver — on top so it's the primary action */}
                    <div className="text-xs font-medium text-muted-foreground">Add a driver</div>

                    <div className="flex items-center gap-2 text-sm">
                        <button type="button" onClick={() => setMode('pick')}
                            className={`px-3 py-1 rounded-md border text-xs ${mode === 'pick' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                            Pick from roster
                        </button>
                        <button type="button" onClick={() => setMode('new')}
                            className={`px-3 py-1 rounded-md border text-xs ${mode === 'new' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                            Add new driver
                        </button>
                    </div>

                    {mode === 'pick' && (
                        <div>
                            {roster === 'loading' && <div className="text-xs text-muted-foreground">Loading roster…</div>}
                            {roster && !Array.isArray(roster) && roster !== 'loading' && (
                                <div className="text-xs text-red-600">Couldn&apos;t load roster — {roster.error}</div>
                            )}
                            {Array.isArray(roster) && activeRoster.length === 0 && (
                                <div className="text-xs text-muted-foreground">No drivers on file for {carrierLabel} yet — add one instead.</div>
                            )}
                            {activeRoster.length > 0 && (
                                <select
                                    value={selectedId ?? ''}
                                    onChange={(e) => setSelectedId(e.target.value ? Number(e.target.value) : null)}
                                    className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                                >
                                    <option value="">Select a driver…</option>
                                    {activeRoster.map(d => (
                                        <option key={d.driverId} value={d.driverId}>{d.name} · {d.phone}</option>
                                    ))}
                                </select>
                            )}
                        </div>
                    )}

                    {mode === 'new' && (
                        <div className="space-y-2">
                            <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Driver name"
                                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
                            <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 555 555 5555"
                                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
                            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email (optional)"
                                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
                            <div className="text-[11px] text-muted-foreground">Saved to {carrierLabel}&apos;s driver roster for future appointments.</div>
                        </div>
                    )}

                    <div className="flex justify-end pt-1">
                        <Button variant="default" size="sm" onClick={handleAdd} disabled={adding}>
                            {adding ? 'Adding…' : 'Add driver'}
                        </Button>
                    </div>

                    {msg && (
                        <div className={`text-xs rounded-md px-3 py-2 ${msg.kind === 'ok' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{msg.text}</div>
                    )}

                    {/* Drivers already on the appointment — titled, below the add controls */}
                    <div className="text-xs font-medium text-muted-foreground pt-2 border-t">Drivers on this appointment</div>
                    {drivers.length > 0 ? (
                        <div className="border rounded-md divide-y">
                            {drivers.map(d => (
                                <div key={d.appointmentDriverId} className="flex items-center justify-between gap-2 px-3 py-2">
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium truncate">{d.name}</div>
                                        <div className="text-xs text-muted-foreground truncate">{d.phone}</div>
                                    </div>
                                    <Button
                                        variant="ghost" size="sm"
                                        onClick={() => handleRemove(d.appointmentDriverId)}
                                        disabled={removingId === d.appointmentDriverId}
                                        className="text-destructive hover:text-destructive shrink-0"
                                    >
                                        {removingId === d.appointmentDriverId ? 'Removing…' : 'Remove'}
                                    </Button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-xs text-muted-foreground border rounded-md px-3 py-2">
                            No drivers on this appointment yet.
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t mt-1">
                    <Button variant="ghost" size="sm" onClick={close} disabled={adding}>Close</Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
